import { useSendEmailConfirmationCode } from "@tandemdiabetes/sugarmate-api-react-client"
import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import {
  useEffect,
  useRef,
} from "react"

import { SendEmailConfirmationCodeErrorToast } from "src/toasts"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

interface Params {
  skipAutoSend: boolean;
  emailAddress: string;
  language: string;
  onSuccessCallback: (emailAddress: string) => void;
}

// this hook automatically requests an email confirmation code
export function useAutoSendEmailConfirmationCode(params: Params) {
  const {
    skipAutoSend,
    emailAddress,
    language,
    onSuccessCallback,
  } = params

  const { showToast } = useToasterContext()
  const sendEmailConfirmationCode = useSendEmailConfirmationCode()

  // automatically attempt to send email verification code on first render
  const hasAutoSentEmail = useRef<boolean>(skipAutoSend)

  useEffect(
    () => {
      if (!sendEmailConfirmationCode || hasAutoSentEmail.current) {
        return
      }

      void (async () => {
        hasAutoSentEmail.current = true

        try {
          await sendEmailConfirmationCode({
            email: emailAddress,
            lang: language,
          })
          onSuccessCallback(emailAddress)
        } catch (e) {
          const sugarmateError = getSugarmateError(e)

          const ErrorToast = injectProps(
            SendEmailConfirmationCodeErrorToast,
            {
              code: sugarmateError?.code,
              status: sugarmateError?.status,
            },
          )

          showToast(
            "send-email-confirmation-code-error-toast",
            ErrorToast,
          )
        }
      })()
    },
    [
      sendEmailConfirmationCode,
      emailAddress,
      language,
      showToast,
      onSuccessCallback,
    ],
  )
}
